import { useMutation } from '@tanstack/react-query'
import { assert } from '@/utils'
import { TX_MSG, calculateFeeWithMinimalDenom, isSelectedCoinMinimalDenom } from '@/wallet-utils'
import { MutationParameters } from './types'
import { useSelectedChain } from '@/contexts/wallet'
import { useSelectedDenom } from '../../../../contexts/denom'
import { CHAIN_INFO_LIST } from '@/config'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { useStake } from '../../StakeProvider'

const useSignRedeemTokenForSharesMutation = ({ setRedeemTokensRaw }: MutationParameters) => {
  const { address, getSigningStargateClient } = useSelectedChain()

  const [denom] = useSelectedDenom()

  const { withdrawLsm } = useStake()

  const handleMutation = async () => {
    assert(address, `You are unable to send token without connecting your ${denom} wallet.`)

    // The user has the option to reverts from either a TokenizeShares or LSM IBC transaction
    const tokenizedDenomToRedeem = withdrawLsm.txHistoryIbcTokenizedDenom || withdrawLsm.txHistoryTokenizedDenom

    const msgRedeemTokensForShares = {
      typeUrl: TX_MSG.MsgRedeemTokensForShares,
      value: {
        amount: {
          amount: withdrawLsm.amount,
          denom: tokenizedDenomToRedeem
        },
        delegatorAddress: address
      }
    }

    const minimalDenom = CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom

    assert(isSelectedCoinMinimalDenom(minimalDenom), `Invalid minimal denom for ${denom}`)

    const client = await getSigningStargateClient()
    const gas = await client.simulate(address, [msgRedeemTokensForShares], '')
    const fee = calculateFeeWithMinimalDenom(minimalDenom, gas)

    return await client.sign(address, [msgRedeemTokensForShares], fee, '')
  }

  const handleSuccess = (raw: TxRaw) => {
    setRedeemTokensRaw(raw)
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignRedeemTokenForSharesMutation }
